/**
 * The staged calculation: the stages a measure is priced in, the blocks they
 * are built from, the library those come out of, and the breakdown a position
 * freezes.
 *
 * One namespace, because the Kalkulation tab, the Preisbausteine branch and
 * the position's breakdown all name the same things — a stage, a row, a
 * reference, the customer price. A heading restated per screen is how the same
 * figure comes to be called two things.
 *
 * What a block IS stays in `BLOCK_TYPE_LABELS` and `BLOCK_ROLE_LABELS`: those
 * are keyed by a Prisma enum member, which is the only key a label for an enum
 * may have.
 */
import { COMMON } from './common'
import { COST_LABELS } from './costs'
import { TERMS } from './terms'

/** Read by the preview's heading and by the note beside it, so the figure the
 *  customer is quoted is named once. */
const CUSTOMER_PRICE = 'Kundenpreis'

export const CALCULATION = {
  /** The switch over the three answers a measure can give. */
  /** The template a measure's stages are seeded from. */
  template: COMMON.template,
  /** The Stufen-Vorlagen section of the Preisbausteine branch. */
  stageTemplates: `${TERMS.stagePlural}-Vorlagen`,
  /** What one stage adds, beside its heading. */
  stageSubtotal: `Summe ${TERMS.stage}`,
  addStage: `${TERMS.stage} hinzufügen`,
  addBlock: `${TERMS.block} hinzufügen`,
  /** The whole price, as the customer is quoted it. Said by the preview and by
   *  the position's frozen breakdown, which state the same figure. */
  customerPrice: CUSTOMER_PRICE,
  /** Everything before margin and tax — what the work costs to buy in. */
  netCost: 'Netto (Einkauf)',

  /** A row that follows the catalogue rather than holding figures of its own:
   *  the badge such a row wears, and the option that makes one. */
  reference: 'Referenz',

  /** How many things a Preisgruppe charges. */
  memberCount: (n: number) =>
    n === 1 ? `1 ${TERMS.member}` : `${n} ${TERMS.memberPlural}`,

  /** The row of a stage. */
  row: {
    /** On a stage holding a row that inserts a whole Preisgruppe. */
    groupReference: `${TERMS.priceGroup} aus Katalog`,
    detach: 'Lösen & anpassen',
    /** Under such a row, before the detach button. */
    libraryNote: `Änderungen an der ${TERMS.priceGroup} wirken in allen ${TERMS.measurePlural}, die sie nutzen.`,
    /** What a mark-up took its percentage of. */
    markUpBasis: (percent: string, basis: string) =>
      `${percent} auf Zwischensumme ${basis}`,
    /** What a budget row charges: the highest bound among the requirement
     *  rows it covers. */
    productBudget: (count: number) =>
      count === 1
        ? 'Budget aus 1 Anforderungs-Variante'
        : `Budget aus ${count} Anforderungs-Varianten`,
    /** Beside either, where the row takes more than one. */
    times: (count: string) => `× ${count}`,
    /** Moving the row to another stage, in its own menu. */
    moveTo: (position: number, name: string) =>
      `Nach ${TERMS.stage} ${position} · ${name}`,
  },

  /** The preview beside the stages: the same measure, priced against one
   *  project. */
  preview: {
    heading: COMMON.preview,
    pickProject: 'Beispielprojekt wählen',
    /** No project chosen yet, so nothing reading one can be worked out. */
    noProject: 'Noch kein Beispielprojekt gewählt.',
    /** What the customer is shown of all this, said where the margin is. */
    internalOnly: `Der ${TERMS.customer} sieht im ${TERMS.configurator} und im PDF nur den ${CUSTOMER_PRICE}. Stufen und ${COST_LABELS.margin} bleiben intern.`,
    /** Which room of the example project a row counting a room's sizes is
     *  read against. */
    exampleRoom: 'Beispielraum',
    exampleRoomHint: 'Für Zeilen, die eine Raumgröße zählen.',
    pickRoom: 'Raum wählen',
    /** The names a stage's expressions may read. */
    /** Why there is no figure: the first term the engine could not work out,
     *  which is the one to fix. */
    onRequestTitle: 'Warum kein Preis zustande kommt',
    /** Where to look: which stage of how many, and the row inside it. */
    blockingStage: (position: number, of: number, name: string) =>
      `${TERMS.stage} ${position} von ${of} · ${name}`,
    /** Which half of the row the engine stopped on. */
    blockingRule: (label: string) => `${label} — Regel`,
    blockingCount: (label: string) => `${label} — Menge`,
    /**
     * What the panel is not showing, said outright.
     *
     * Without it a reader has two wrong questions: whether the posts before
     * this one are broken too, and whether the ones after are. The engine
     * stops at the first that does not work, so the first are fine and the
     * rest were never looked at.
     */
    onRequestScope:
      'Die Rechnung hält beim ersten Posten an, der nicht geht. Alles davor hat gerechnet, alles danach wurde nicht mehr geprüft.',
    /** Where the customer price would stand. */
    noPrice: COST_LABELS.priceOnRequest,
    missingAmount: 'Kein Betrag hinterlegt.',
    missingPercent: 'Kein Prozentsatz hinterlegt.',
    missingFormula: 'Keine Formel hinterlegt.',
    missingCount: 'Die Menge ist nicht hinterlegt.',
    /** The expression is there but produced nothing — the panel under it says
     *  what the evaluation made of it. */
    formulaFailed: 'Die Formel ergibt keinen Wert.',
    countFailed: 'Der Mengen-Ausdruck ergibt keinen Wert.',
    /** The disclosure under the figures: every term, in the order the engine
     *  took them, with what it was worked out of. */
    howItAdds: 'Rechenweg',
    howItAddsHint:
      'Jeder Posten in der Reihenfolge, in der gerechnet wurde — die Regel, die gelesenen Werte und was dabei herauskommt.',
    /** The three lines of one step. */
    stepRule: 'Regel',
    stepResult: 'ergibt',
    /** The arithmetic the step closes with. */
    stepProduct: (rate: string, count: string, total: string) =>
      `${rate} × ${count} = ${total}`,
    stepShare: (basis: string, percent: string, total: string) =>
      `${percent} von ${basis} = ${total}`,
    /** What the expression read. "Scope" is the editor's word for somebody
     *  writing one; this is read by somebody checking a price. */
    readValues: 'Gelesene Werte',
    /** The two names a staged expression may read, as the completion list
     *  and the reference panel head them. */
    stageVariable: (position: number) => `Summe ${TERMS.stage} ${position}`,
    subtotalVariable: `Summe aller ${TERMS.stagePlural} davor`,
  },

  /** The dialog that puts a row into a stage, or changes one. */
  add: {
    title: 'Zeile hinzufügen',
    editTitle: 'Zeile bearbeiten',
    /** Which stage it lands in, under the title. */
    toStage: (position: number, name: string) =>
      `zu ${TERMS.stage} ${position} · ${name}`,
    /** The two ways a row gets its terms: out of the catalogue, or written
     *  here for this measure alone. */
    fromCatalogue: 'Aus Katalog',
    direct: 'Direkt eingeben',
    /** What a row written here is, and is not. */
    directHint: `Gilt nur für diese ${TERMS.measure}. Was mehrere ${TERMS.measurePlural} teilen, gehört in den Katalog.`,
    /** The budget a measure sets, which is none of the catalogue's. */
    budgetKind: 'Produktbudget',
    /** The choice between following the catalogue and holding a copy. */
    insertAs: 'Einfügen als',
    asReferenceHint: 'Änderungen im Katalog wirken hier mit',
    asCopy: 'Kopie',
    asCopyHint: `eigenständig, nur für diese ${TERMS.measure}`,
    /** The requirement rows a budget row charges the highest bound of. */
    budget: 'Anforderungen, aus denen das Budget kommt',
    budgetHint:
      'Die Zeile rechnet immer mit dem höchsten Maximalpreis der gewählten Varianten — auch nachdem der Kunde bemustert hat.',
    budgetEmpty: `Diese ${TERMS.measure} hat noch keine Anforderungs-Varianten.`,
    variantNoBound: `Kein Maximalpreis — die ${TERMS.measure} wäre auf Anfrage`,
    /** Editing a row that points at the catalogue: the terms are not here. */
    followsCatalogue:
      'Was diese Zeile berechnet, steht im Katalog. Hier lässt sich nur einstellen, wie oft sie es nimmt.',
    followsGroup: `Diese Zeile folgt einer ${TERMS.priceGroup}. Löse sie, um einzelne ${TERMS.memberPlural} zu ändern.`,
  },

  /** How often a row takes what it charges. */
  count: {
    /** The three ways to say it, in the order they are reached for. */
    fromProject: 'Aus dem Projekt',
    fixed: 'Feste Zahl',
    formula: TERMS.formula,
    pick: 'Größe wählen',
    /** Nothing the project measures fits the unit — an hourly rate has no
     *  hours anywhere in the object. */
    noneFit: (unit: string) =>
      `Das Projekt misst nichts in ${unit}. Nimm eine feste Zahl oder eine Formel.`,
    /** What the free expression is for, under the field. */
    formulaHint:
      'Darf alles nennen, was eine Preisformel nennen darf — auch eine Formel aus der Sammlung.',
  },

  /** The pane of one Preisbaustein or one Preisgruppe. */
  pane: {
    terms: 'Was es berechnet',
    catalogueName: 'Katalogname',
    catalogueNameHint:
      'Darüber findet eine Zeile den Eintrag wieder. Eindeutig, und keine Formel darf so heißen.',
    role: 'Zählt in der Auswertung als',
    groups: TERMS.priceGroupPlural,
    groupsHint: `Eine ${TERMS.priceGroup} fügt ihre ${TERMS.memberPlural} als eine Zeile ein. Derselbe ${TERMS.block} darf in mehreren liegen.`,
    addGroup: `${TERMS.priceGroup} hinzufügen`,
    /** The dialog that picks one of the groups it is not in yet. */
    pickGroup: `${TERMS.priceGroup} wählen`,
    allGroupsHeld: `Schon in allen ${TERMS.priceGroupPlural}.`,
    members: TERMS.memberPlural,
    membersHint:
      'Die Reihenfolge entscheidet: ein Aufschlag rechnet auf alles, was vor ihm steht.',
    addMember: `${TERMS.member} hinzufügen`,
    noMembers: `Noch keine ${TERMS.memberPlural}.`,
  },

  /** The Preisbausteine branch of the catalogue. */
  library: {
    /** What a Preisgruppe charges, as its column counts it. */
    members: TERMS.memberPlural,
    /** The column saying what a block computes, in one line. */
    computation: 'Berechnung',
    /** What a mark-up block states, where no stage gives it a basis yet. */
    markUpOf: (percent: string) => `${percent} × subtotal`,
  },

  /** The breakdown a position carries, in the planning. */
  position: {
    /** Heads the frozen breakdown: the figures as they were when the position
     *  was quoted, not as the catalogue states them today. */
    frozen: 'Aufschlüsselung (beim Einfrieren)',
    /** What the whole of that breakdown came to — the figure the position was
     *  quoted at, before anybody moved it. */
    cataloguePrice: 'Katalogpreis',
    afterDiscount: 'Nach Rabatt',
    priceAfterDiscount: (discount: string) => `Preis nach ${discount} Rabatt`,
    vatInIt: `davon ${COST_LABELS.vat}`,
    marginAfterDiscount: `${COST_LABELS.margin} nach Rabatt`,
    /** What the discount cost in margin, under the figure. */
    marginGivenUp: (before: string, lost: string) =>
      `statt ${before} · Rabatt kostet ${lost} ${COST_LABELS.margin}`,
    /** Under the table, where the breakdown is shown. */
    internalOnly: `Nur intern sichtbar. Der ${TERMS.customer} sieht im PDF nur den Preis der Position.`,
  },

  /** The filter the migration's leftovers are worked through by. */
  needsReview: 'Kalkulation prüfen',
  needsReviewHint:
    'Der frühere Preis wurde als ein Baustein übernommen. Marge und Steuern sind darin noch nicht getrennt.',
  /** Clears the flag once somebody has looked. */
  needsReviewDone: COMMON.checked,
  /** Seeds the stages from a template, in the picker over them. */
  applyTemplate: 'Vorlage anwenden',
  /** Asked before a template replaces stages somebody built by hand. */
  replaceTitle: 'Kalkulation ersetzen?',
  replaceConfirm: (count: number) =>
    count === 1
      ? 'Diese Vorlage ersetzt die eine Zeile, die diese Maßnahme schon hat.'
      : `Diese Vorlage ersetzt die ${count} Zeilen, die diese Maßnahme schon hat.`,
  /** What the two answers that compute nothing mean, where the stages would
   *  otherwise be. */
  includedHint: `Diese ${TERMS.measure} ist im Basispreis enthalten und erhöht keine Summe.`,
  onRequestHint: `Diese ${TERMS.measure} wird auf Anfrage kalkuliert. Es werden keine Stufen ausgewertet.`,
  deleteStageConfirm: `Die ${TERMS.stage} und alle ihre Zeilen werden gelöscht. Bausteine aus der Bibliothek bleiben erhalten.`,
  deleteSlotTitle: 'Zeile entfernen',
  deleteSlotConfirm:
    'Die Zeile wird aus dieser Stufe entfernt. Ein Baustein aus der Bibliothek bleibt dort erhalten.',

  errors: {
    slotNeedsOneSource: `Eine Zeile berechnet genau eines: einen ${TERMS.block}, eine Kopie davon, eine ${TERMS.priceGroup}, ein ${TERMS.product}, eine ${TERMS.labourItem}, das Produktbudget oder eigene Werte.`,
    slotAlreadyDetached: 'Diese Zeile führt schon eigene Werte.',
    slotFollowsTheLibrary:
      'Diese Zeile folgt einem Bibliothekseintrag. Löse sie zuerst, um ihre Werte zu ändern.',
    slotReferencesNothing:
      'Diese Zeile verweist auf keinen Bibliothekseintrag — es gibt nichts zu lösen.',
    slotMoveAcrossOwners: `Eine Zeile wandert zwischen den ${TERMS.stagePlural} einer ${TERMS.measure}, nicht zwischen ${TERMS.measurePlural}.`,
    blockStillReferenced: (count: number) =>
      count === 1
        ? `Dieser ${TERMS.block} wird noch von einer Zeile verwendet. Löse sie zuerst.`
        : `Dieser ${TERMS.block} wird noch von ${count} Zeilen verwendet. Löse sie zuerst.`,
    groupStillReferenced: (count: number) =>
      count === 1
        ? `Diese ${TERMS.priceGroup} wird noch von einer Zeile verwendet. Löse sie zuerst.`
        : `Diese ${TERMS.priceGroup} wird noch von ${count} Zeilen verwendet. Löse sie zuerst.`,
    measureHoldsOwnCalculation: (count: number) =>
      `Diese ${TERMS.measure} hat schon ${count} eigene ${
        count === 1 ? 'Zeile' : 'Zeilen'
      }. Eine Vorlage anzuwenden ersetzt sie.`,
    sortNeedsEveryRow:
      'Gib alle Zeilen in der neuen Reihenfolge an — die Reihenfolge entscheidet den Preis.',
    slotBudgetNeedsAMeasure: `Eine Zeile einer Vorlage hat keine ${TERMS.measure}, aus deren Anforderungen sie ein Budget nehmen könnte.`,
    slotBudgetForeignVariant: `Eine Zeile nimmt das Budget aus den Anforderungen ihrer eigenen ${TERMS.measure}.`,
    groupMemberNeedsOneTarget: `Ein ${TERMS.member} einer ${TERMS.priceGroup} rechnet mit einem ${TERMS.block}, einem ${TERMS.product} oder einer ${TERMS.labourItem} — mit genau einem.`,
    labourItemStillCharged: (count: number) =>
      count === 1
        ? `Diese ${TERMS.labourItem} wird noch von einem ${TERMS.block} berechnet. Nimm ihn zuerst aus dem Preis, oder stelle die ${TERMS.labourItem} inaktiv.`
        : `Diese ${TERMS.labourItem} wird noch von ${count} ${TERMS.blockPlural} berechnet. Nimm sie zuerst aus dem Preis, oder stelle die ${TERMS.labourItem} inaktiv.`,
  },
} as const
